import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import type { Article, PlaybackSnapshot } from '@/domain/Models';
import {
  createRandomBag,
  decideArticleEndAction,
  nextArticleId,
  nextFromBag,
  prevArticleId,
  prevFromBag,
} from '@/domain/articleQueue';

/** 本会话内按文章记住「单曲循环」，离开播放器或刷新后默认关闭。 */
const loopByArticleId = new Map<string, boolean>();

type PlayerNavState = {
  random?: boolean;
  bag?: string[];
  playAfter?: boolean;
};

function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function progressText(a: Article): string {
  if (a.durationMs <= 0) return '';
  const pct = Math.min(100, Math.round((a.lastPositionMs / a.durationMs) * 100));
  return `已听 ${pct}%`;
}

// =====================================================================
// 文章列表 ("文章 · 磨耳朵" + 导入菜单 + 随机练习 + 列表)
// =====================================================================
export function ArticleListPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showFormat, setShowFormat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [todayDone, setTodayDone] = useState(0);
  const [todayTarget, setTodayTarget] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLInputElement>(null);
  const subtitleRef = useRef<HTMLInputElement>(null);
  const [pendingAudio, setPendingAudio] = useState<File | null>(null);
  const [pendingSubtitle, setPendingSubtitle] = useState<File | null>(null);
  const [localTitle, setLocalTitle] = useState('');
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [list, done, settings] = await Promise.all([
        appServices.article.list(),
        appServices.article.todayCompletedCount(),
        appServices.settings.get(),
      ]);
      setArticles(list);
      setTodayDone(done);
      setTodayTarget(settings.dailyArticleTaskCount);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function startRandom() {
    const list = await appServices.article.list();
    const bag = createRandomBag(list.map((a) => a.id));
    const id = bag[0];
    if (id) navigate(`/article/${id}`, { state: { random: true, bag } });
  }

  async function importPack(file: File) {
    setImportMsg('正在导入文章包…');
    setMenuOpen(false);
    try {
      const r = await appServices.article.importPackage(file);
      setImportMsg(r.ok ? '文章包导入成功' : `导入失败：${r.errorCode}`);
      await refresh();
    } catch (e) {
      setImportMsg(`导入失败：${String(e)}`);
    }
  }

  async function removeArticle(a: Article) {
    if (!window.confirm(`删除这篇导入文章？`)) return;
    const r = await appServices.article.deleteImported(a.id);
    if (r.ok) await refresh();
    else setError(r.errorCode ?? '删除失败');
  }

  return (
    <div className="page stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>文章朗读 · 磨耳朵</strong>
        <button
          type="button"
          className="secondary"
          aria-label="导入文章"
          onClick={() => setMenuOpen((v) => !v)}
        >
          ＋
        </button>
      </div>
      {menuOpen && (
        <div className="card stack">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              fileRef.current?.click();
            }}
          >
            导入 .bessarticle 文章包
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setMenuOpen(false);
              audioRef.current?.click();
            }}
          >
            导入本地音频（M4A/MP3/WAV）
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setMenuOpen(false);
              setShowFormat(true);
            }}
          >
            查看格式说明
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".bessarticle,application/zip"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void importPack(f);
          e.target.value = '';
        }}
      />
      <input
        ref={audioRef}
        type="file"
        accept="audio/mp4,audio/mpeg,audio/wav,.m4a,.mp3,.wav"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            setPendingAudio(f);
            setLocalTitle(f.name.replace(/\.[^.]+$/, ''));
            setPendingSubtitle(null);
          }
          e.target.value = '';
        }}
      />
      <input
        ref={subtitleRef}
        type="file"
        accept=".srt,.lrc,text/plain"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setPendingSubtitle(f);
          e.target.value = '';
        }}
      />
      {pendingAudio && (
        <section className="card stack">
          <strong>导入本地听力</strong>
          <label className="stack">
            <span className="muted">标题</span>
            <input value={localTitle} onChange={(e) => setLocalTitle(e.target.value)} />
          </label>
          <div className="muted">音频：{pendingAudio.name}</div>
          <div className="muted">字幕：{pendingSubtitle ? pendingSubtitle.name : '未选择（可选）'}</div>
          <button type="button" className="secondary" onClick={() => subtitleRef.current?.click()}>
            选择 SRT / LRC
          </button>
          <button
            type="button"
            onClick={() => {
              void (async () => {
                setImportMsg('正在导入本地音频…');
                const r = await appServices.article.importLocalArticle(
                  localTitle || pendingAudio.name,
                  localTitle || pendingAudio.name,
                  pendingAudio,
                  pendingSubtitle ?? undefined,
                );
                setImportMsg(r.ok ? '本地音频已导入' : `导入失败：${r.errorCode}`);
                setPendingAudio(null);
                setPendingSubtitle(null);
                await refresh();
              })();
            }}
          >
            确认导入
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setPendingAudio(null);
              setPendingSubtitle(null);
            }}
          >
            取消
          </button>
        </section>
      )}
      {showFormat && (
        <div className="card stack">
          <strong>.bessarticle 文章包格式</strong>
          <div className="muted">
            由储能英语实战各端导出的文章包（zip）：包含 manifest 与逐句字幕的音频。导入后可在「文章 · 磨耳朵」中收听。
          </div>
          <button type="button" onClick={() => setShowFormat(false)}>
            知道了
          </button>
        </div>
      )}
      {importMsg && (
        <div className="card">
          <div className="muted">{importMsg}</div>
          <button type="button" onClick={() => setImportMsg(null)}>
            知道了
          </button>
        </div>
      )}

      <div className="card stack">
        <div className="muted">
          今日文章任务 {Math.min(todayDone, todayTarget)}/{todayTarget}
          {todayDone >= todayTarget ? ' · 今日任务已完成' : ''}
        </div>
        <strong>随机练习</strong>
        <div className="muted">随机播放储能文章磨耳朵，整袋播放前不重复。</div>
        <button type="button" onClick={() => void startRandom()}>
          随机播放
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}
      {articles.length === 0 ? (
        <div className="stack">
          <div className="muted">文章库为空。点击右上角 ＋，导入 .bessarticle 文章包。</div>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              fileRef.current?.click();
            }}
          >
            导入 .bessarticle 文章包
          </button>
        </div>
      ) : (
        <div className="stack">
          {articles.map((a) => (
            <div className="list-item stack" key={a.id} style={{ gap: 6 }}>
              <button
                type="button"
                className="grow"
                style={{ textAlign: 'left', background: 'none', border: 'none', padding: 0 }}
                onClick={() => navigate(`/article/${a.id}`, { state: { random: false } })}
              >
                <div style={{ fontWeight: 600 }}>{a.titleZh || a.title}</div>
                <div className="muted">
                  {a.titleZh ? a.title : ''} {fmt(a.durationMs)} · {a.topic}
                </div>
              </button>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="muted">{progressText(a)}</div>
                {a.source !== 'BUNDLED' && (
                  <div className="row" style={{ gap: 8 }}>
                    <span className="chip">导入文章</span>
                    <button
                      type="button"
                      className="secondary"
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                      onClick={() => void removeArticle(a)}
                    >
                      删除
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// 文章播放器 ("文章磨耳朵 / 随机磨耳朵", 底部固定播放条)
// =====================================================================
export function ArticlePlayerPage() {
  const { articleId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as PlayerNavState | null) ?? null;
  const isRandom = navState?.random === true;

  const [article, setArticle] = useState<Article | null>(null);
  const [all, setAll] = useState<Article[]>([]);
  const [snap, setSnap] = useState<PlaybackSnapshot>(appServices.audio.snapshot());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loopCurrent, setLoopCurrent] = useState(() => loopByArticleId.get(articleId) === true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const skipRef = useRef<(direction: 'next' | 'prev', opts?: { forcePlay?: boolean }) => Promise<void>>(
    async () => undefined,
  );
  const loopRef = useRef(loopCurrent);
  loopRef.current = loopCurrent;
  const settingsRef = useRef({ autoPlayNextArticle: true, playbackSpeed: 1 });
  const completedHandledRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, list] = await Promise.all([
        appServices.article.get(articleId),
        appServices.article.list(),
      ]);
      setArticle(a);
      setAll(list);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, [articleId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setLoopCurrent(loopByArticleId.get(articleId) === true);
    completedHandledRef.current = null;
  }, [articleId]);

  useEffect(() => {
    appServices.audio.setLoop(loopCurrent);
  }, [loopCurrent, articleId]);

  useEffect(() => {
    void appServices.settings.get().then((s) => {
      settingsRef.current = {
        autoPlayNextArticle: s.autoPlayNextArticle,
        playbackSpeed: s.playbackSpeed,
      };
    });
  }, [articleId]);

  useEffect(() => {
    return () => {
      appServices.audio.setSkipHandlers(null);
      appServices.audio.setAdvanceHandler(null);
      appServices.audio.setMediaSession(null);
      void appServices.audio.stop();
    };
  }, []);

  useEffect(() => {
    const unsub = appServices.audio.subscribe(setSnap);
    return () => unsub();
  }, []);

  // 自动保存播放进度 (防抖)。切下一篇时先播新音频再跳转，必须对上 assetId。
  const lastSaveRef = useRef(0);
  useEffect(() => {
    if (!article) return;
    if (snap.positionMs === 0) return;
    if (snap.assetId && snap.assetId !== article.audioAssetId) return;
    const now = Date.now();
    if (now - lastSaveRef.current < 800) return;
    lastSaveRef.current = now;
    void appServices.article.saveProgress(article.id, snap.positionMs, false);
  }, [snap.positionMs, snap.assetId, article]);

  skipRef.current = async (direction, opts) => {
    if (!article) return;
    const ids = all.map((a) => a.id);
    let nextId: string | null = null;
    let bag = navState?.bag;
    if (isRandom) {
      if (direction === 'next') {
        const r = nextFromBag(bag ?? createRandomBag(ids, article.id), article.id, ids);
        nextId = r.nextId;
        bag = r.bag;
      } else {
        const r = prevFromBag(bag ?? createRandomBag(ids, article.id), article.id);
        nextId = r.prevId;
      }
    } else {
      nextId =
        direction === 'next' ? nextArticleId(ids, article.id) : prevArticleId(ids, article.id);
    }
    if (!nextId || nextId === article.id) return;
    const nextArticle = all.find((a) => a.id === nextId);
    const shouldPlay =
      opts?.forcePlay === true ||
      snap.state === 'PLAYING' ||
      snap.state === 'PAUSED' ||
      snap.state === 'BUFFERING';
    const prevId = article.id;
    const prevPos = opts?.forcePlay === true ? article.durationMs : snap.positionMs;
    const markDone = opts?.forcePlay === true;
    if (shouldPlay && nextArticle) {
      await appServices.audio.play(nextArticle.audioAssetId, settingsRef.current.playbackSpeed);
    }
    navigate(`/article/${nextId}`, {
      state: { random: isRandom, bag },
    });
    void appServices.article.saveProgress(prevId, prevPos, markDone);
    if (markDone) void appServices.article.markCompletedToday(prevId);
  };

  // 预取下一篇，ended 切歌时不再等读盘
  useEffect(() => {
    if (!article || all.length === 0) return;
    const ids = all.map((a) => a.id);
    let nextId: string | null;
    if (isRandom) {
      nextId = nextFromBag(
        navState?.bag ?? createRandomBag(ids, article.id),
        article.id,
        ids,
      ).nextId;
    } else {
      nextId = nextArticleId(ids, article.id);
    }
    const next = nextId ? all.find((a) => a.id === nextId) : undefined;
    if (next) void appServices.audio.prefetch(next.audioAssetId);
  }, [article, all, isRandom, navState?.bag]);

  // 播放结束 → 标记完成；自动下一篇在 audio ended 回调里立刻切歌
  useEffect(() => {
    if (!article) return;
    if (snap.state !== 'COMPLETED') return;
    if (snap.assetId !== article.audioAssetId) return;
    const token = `${article.id}:${snap.assetId}`;
    if (completedHandledRef.current === token) return;
    completedHandledRef.current = token;
    void appServices.article.saveProgress(article.id, article.durationMs, true);
    void appServices.article.markCompletedToday(article.id);
  }, [snap.state, snap.assetId, article]);

  useEffect(() => {
    if (!article) return;
    setExpanded(new Set());
    appServices.audio.setMediaSession({
      title: article.titleZh || article.title,
      artist: '储能英语实战',
      artworkUrl: '/icons/icon-192.png',
    });
    appServices.audio.setSkipHandlers({
      onNext: () => skipRef.current('next'),
      onPrevious: () => skipRef.current('prev'),
    });
    appServices.audio.setAdvanceHandler(() => {
      const action = decideArticleEndAction(
        loopRef.current,
        settingsRef.current.autoPlayNextArticle,
      );
      if (action !== 'next') return;
      void skipRef.current('next', { forcePlay: true });
    });
  }, [article]);

  useEffect(() => {
    if (!article || article.id !== articleId || !navState?.playAfter) return;
    void (async () => {
      const speed = (await appServices.settings.get()).playbackSpeed;
      await appServices.audio.play(article.audioAssetId, speed);
    })();
    navigate(location.pathname, {
      replace: true,
      state: { random: navState.random, bag: navState.bag },
    });
  }, [article, articleId, navState?.playAfter, location.pathname, navigate, navState?.random, navState?.bag]);

  // 当前段落高亮 + 自动滚动
  const activeIdx = article
    ? article.paragraphs.findIndex(
        (p) =>
          p.startMs != null &&
          p.endMs != null &&
          snap.positionMs >= p.startMs &&
          snap.positionMs < p.endMs,
      )
    : -1;
  useEffect(() => {
    if (activeIdx < 0 || !scrollRef.current) return;
    const el = scrollRef.current.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIdx]);

  if (!article) {
    return (
      <div className="page">
        {error ? <div className="error-box">{error}</div> : <div className="muted">加载中…</div>}
      </div>
    );
  }

  const hasSubtitles = article.paragraphs.some((p) => p.textZh?.trim());
  const canSkip = isRandom || all.length > 1;

  async function toggleParagraph(i: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  async function playArticle(a: Article) {
    const speed = (await appServices.settings.get()).playbackSpeed;
    await appServices.audio.play(a.audioAssetId, speed);
    if (a.lastPositionMs > 0) {
      await appServices.audio.seekTo(a.lastPositionMs);
    }
  }

  return (
    <div className="app-shell no-nav" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="page stack" style={{ flex: 1, paddingBottom: 130 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button className="secondary" type="button" onClick={() => navigate('/article')}>
            ← 返回
          </button>
          <span className="chip">{isRandom ? '随机磨耳朵' : '文章磨耳朵'}</span>
        </div>
        <div className="muted">英文全文 · 点击有翻译的句子查看中文</div>

        {!hasSubtitles && (
          <div className="card muted">
            此文章未附字幕，可进行纯听练习。播放、暂停、拖动进度和下一篇功能仍可正常使用。
          </div>
        )}

        {error && <div className="error-box">{error}</div>}

        <div className="stack" ref={scrollRef}>
          {article.paragraphs.map((p, i) => (
            <div
              key={i}
              className={`card${i === activeIdx ? ' paragraph-active' : ''}`}
              style={{ cursor: p.textZh ? 'pointer' : 'default' }}
              onClick={() => {
                if (p.textZh) void toggleParagraph(i);
              }}
            >
              <div>{p.textEn}</div>
              {expanded.has(i) && p.textZh && <div className="muted">{p.textZh}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* 底部固定播放条 */}
      <div className="article-player-bar">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {article.titleZh || article.title}
          </div>
          <button
            type="button"
            className="article-loop-toggle secondary"
            aria-pressed={loopCurrent}
            aria-label="单曲循环"
            onClick={() => {
              const next = !loopCurrent;
              setLoopCurrent(next);
              loopByArticleId.set(article.id, next);
              appServices.audio.setLoop(next);
              if (next && snap.state === 'COMPLETED') {
                void (async () => {
                  const speed = settingsRef.current.playbackSpeed;
                  await appServices.audio.play(article.audioAssetId, speed);
                })();
              }
            }}
          >
            单曲循环
          </button>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(1, article.durationMs)}
          value={Math.min(snap.positionMs, article.durationMs)}
          onChange={(e) => void appServices.audio.seekTo(Number(e.target.value))}
          aria-label="播放进度"
        />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="muted">
            {fmt(snap.positionMs)} / {fmt(article.durationMs)}
          </span>
          <div className="article-player-bar-transport">
            <button
              type="button"
              className="secondary"
              aria-label="上一篇"
              disabled={!canSkip}
              onClick={() => void skipRef.current('prev')}
            >
              上一篇
            </button>
            <button
              type="button"
              className="secondary"
              aria-label="播放或暂停"
              onClick={async () => {
                if (snap.state === 'PLAYING') await appServices.audio.pause();
                else if (snap.state === 'PAUSED' || snap.state === 'BUFFERING')
                  await appServices.audio.resume();
                else await playArticle(article);
              }}
            >
              {snap.state === 'PLAYING' ? '⏸' : '▶'}
            </button>
            <button
              type="button"
              className="secondary"
              aria-label="下一篇"
              disabled={!canSkip}
              onClick={() => void skipRef.current('next')}
            >
              下一篇
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
